/**
 * The Signet panel config document, and the postMessage contract between an
 * editor (the admin customizer) and `preview.html`.
 *
 * Like `protocol.ts`, this file is the ONLY definition of both. The admin,
 * the preview entry and the production panel all import it, so a renamed
 * field breaks the build instead of silently breaking a merchant's panel.
 *
 * The config is a DOCUMENT, not code: everything in it is data a merchant
 * chose in the customizer (or, in phase 1, that we wrote into their app
 * config by hand). The renderer is the sole interpreter, which is what lets
 * the admin preview be pixel-identical to production — both feed the same
 * document to the same renderer.
 */
import { PROTOCOL_NAMESPACE, PROTOCOL_VERSION } from "./protocol";

/* ------------------------------------------------------------------ */
/* The document                                                        */
/* ------------------------------------------------------------------ */

export type PanelMode = "dark" | "light";
export type PanelTint = "neutral" | "green" | "plum" | "slate" | "custom";
export type PatternType = "halo" | "beam" | "contour" | "dotgrid" | "arc" | "bloom" | "none";
export type PatternCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface PanelPattern {
    type: PatternType;
    /** Which corner the motif radiates from. Ignored by `beam`/`bloom`/`none`. */
    corner?: PatternCorner;
    /** Multiplier on the motif's period. Clamped to [0.5, 2]. */
    scale?: number;
    /**
     * Opacity on the whole layer, 0–1 of the AA-measured ceiling — 1 means
     * "as strong as contrast allows", never stronger. The renderer owns the
     * ceiling (4.5% ink on dark, 5% on light); the merchant only scales
     * within it.
     */
    intensity?: number;
}

export interface PanelBackground {
    mode: PanelMode;
    tint: PanelTint;

    /**
     * Surface colour for `tint: "custom"`, hex. The renderer mixes the two
     * darker steps of the ramp from it; the accent stays `config.accent`,
     * because in every preset dark mode pairs a dark surface with a light
     * accent and light mode does the reverse — a contrast decision the
     * surface cannot imply. Ignored for the four named tints.
     */
    base?: string;
    pattern: PanelPattern;
}

/**
 * One placed widget. `type` is the catalog id (`value.offer_hero`);
 * `rows` is its claimed height in 60px rows; `settings` is the widget's own
 * schema from the admin-customization catalog, opaque to the layout layer.
 */
export interface WidgetInstance {
    type: string;
    rows: number;
    settings?: Record<string, unknown>;
    /**
     * The merchant switched this widget off. It keeps its rows and draws
     * nothing — hold-position, so hiding one widget never shifts the ones
     * below it. A paint flag, never a layout one.
     *
     * This travels on the wire rather than being resolved in the editor
     * because the shopper panel has to honour it too: dropping the widget at
     * publish time would break hold-position, and keeping it would ignore the
     * merchant.
     */
    hidden?: boolean;
}

/**
 * A fixed house for widgets. Sections stack top to bottom and their `rows`
 * must sum to the panel's row count (10 on desktop). Widgets stack inside and
 * may not exceed the section's rows — the editor enforces this before a
 * config is ever saved; the renderer clips as a last resort and reports it.
 */
export interface PanelSection {
    id: string;
    rows: number;
    widgets: WidgetInstance[];
}

/**
 * The store's own identity, from its settings — not authored in the panel.
 *
 * It lives on the CONFIG rather than in a widget's settings because it is one
 * fact about the store, not a choice about one widget: the same logo serves
 * every panel, and changing it in settings has to reach all of them without
 * anyone re-editing a document. A widget that asked a merchant to retype their
 * brand name was asking them to maintain a second copy of it.
 *
 * Fields map to the admin's setting keys: `brand.logo`, `brand.iconic_logo`
 * and `brand.brand_name`.
 */
export interface PanelBrand {
    /** The full lockup, usually mark plus wordmark. */
    logo_url?: string;
    /** The mark on its own, for tight rows and square placements. */
    icon_url?: string;
    /** Last resort when the store has uploaded neither image. */
    name?: string;
}

export interface SignetPanelConfig {
    /** Bumped only on a breaking change to this document's shape. */
    version: 1;
    /** One accent; every derived tone re-mixes from it. Hex. */
    accent?: string;
    background: PanelBackground;
    sections: PanelSection[];

    /** Store identity, injected by whoever assembles the config. */
    brand?: PanelBrand;
}

/** The desktop panel's row budget. A section layout must total exactly this. */
export const PANEL_ROWS = 10;
export const PANEL_ROW_PX = 60;
export const PANEL_W = 520;
export const PANEL_USABLE_W = 440;

/* ------------------------------------------------------------------ */
/* Mobile                                                              */
/* ------------------------------------------------------------------ */

/**
 * The phone surface.
 *
 * On a phone the panel is a banner above the form, not a column beside it,
 * and the merchant designs it SEPARATELY — `PanelDraft` in the editor carries
 * one document per device. A phone has room for about a third of what a
 * 600px column holds, and deriving that third automatically always cut
 * somebody's best line; the merchant is the one who should choose what
 * survives.
 *
 * Everything else about the surface is the same object: same sections, same
 * widgets, same renderer. Only the two numbers below differ.
 *
 * 360px is the Indian Android floor (Redmi, Galaxy A). 390 proves nothing.
 */

/**
 * The phone panel's row budget, and what a row is worth in pixels.
 *
 * A REAL height, not a content allowance. The strip used to flow — every
 * widget as tall as it drew — so a merchant toggling the unlock ladder
 * between its stacked and slider forms resized the entire sign-in card under
 * their cursor. A panel whose height depends on its contents is a panel that
 * moves the form, and the form is the only thing on this screen a shopper
 * came for.
 *
 * 48px, measured, not chosen. The first build made a phone row 32px on the
 * theory that a smaller screen wants a finer grain, and measuring every widget
 * killed it: 9 of the 24 could not fit at ANY height their catalog allows.
 * The second used the desktop 60 and was honest but loose — the offer draws
 * 85px of ink in a 120px box, so a third of the panel was air.
 *
 * 48 is what the phone compositions actually ask for: the offer needs 43 per
 * row over two, the value chip 48, the trust strip 42, the footer line 29.
 * Four rows of it plus the design's 40px of padding is a 232px panel — the
 * height the design draws — and every one of those widgets still clears its
 * box.
 *
 * Four rows is also exactly what the design's composition costs: a one-row
 * lockup, a two-row offer, a one-row value chip. Nothing spare — a merchant
 * who wants a fifth row has to free one, which is the honest shape of a
 * phone. Raising the budget pushes the form down an 812px screen, which is
 * the one thing a sign-in sheet may not do.
 */
export const MOBILE_ROWS = 4;
export const MOBILE_ROW_PX = 50;
export const MOBILE_W = 360;

/** How long a rotating widget holds before it advances. */
export const MOBILE_PAGE_MS = 4200;

/** Rows a surface gives the merchant, and what one is worth. */
export const SURFACE_ROWS = {
    desktop: { rows: PANEL_ROWS, rowPx: PANEL_ROW_PX },
    mobile: { rows: MOBILE_ROWS, rowPx: MOBILE_ROW_PX },
} as const;

/**
 * Which surface a document is for.
 *
 * Named here rather than spelled out at each use because it is the key of
 * SURFACE_ROWS: adding a third surface should break every switch on it, not
 * leave a handful of string unions quietly two-valued. `PanelLayout` in the
 * renderer is the same set under the name the React props use.
 */
export type PanelSurface = keyof typeof SURFACE_ROWS;

/* ------------------------------------------------------------------ */
/* Popup geometry                                                      */
/* ------------------------------------------------------------------ */

/**
 * The desktop card, in CSS pixels.
 *
 * Here rather than beside the component because the HOST decides how wide the
 * popup's frame is — it is the only side that can see the browser viewport,
 * since everything the card knows is its own iframe. The SDK and the renderer
 * therefore have to agree on this number exactly, and they used to agree by
 * each holding a copy.
 */
export const POPUP_W = 960;
export const POPUP_H = 600;

/**
 * The most a phone sheet will stretch to.
 *
 * The sheet is drawn for a phone: a banner over a single column. Given nine
 * tenths of a half-maximised desktop window it would happily become 900px of
 * that, which is not a wider phone, it is a broken one.
 */
export const MOBILE_SHEET_MAX = 560;

/** The overlay's gutter, each side, so the card is never welded to an edge. */
export const POPUP_GUTTER = 20;

/**
 * How wide the popup should be on a viewport `vw` pixels across.
 *
 * The desktop split renders at exactly its designed width or not at all: its
 * two columns are fixed, so a frame narrower than 960 does not compress it, it
 * clips it. Below that the phone sheet takes over and is FLUID — nine tenths
 * of the screen, which is the design's own proportion (342 of a 390 phone),
 * rather than a fixed 342 that reads as a stamp in the middle of anything
 * wider.
 */
export function popupWidth(vw: number): number {
    if (vw >= POPUP_W + POPUP_GUTTER * 2) return POPUP_W;
    return Math.min(Math.round(vw * 0.9), MOBILE_SHEET_MAX);
}

/**
 * Which layout a card `px` wide should draw.
 *
 * Takes the CARD's width, not the viewport's: inside the popup that is all
 * there is to go on, and the host has already applied `popupWidth`, so the
 * desktop split is only ever asked for at exactly the width it needs.
 */
export function layoutForWidth(px: number): PanelSurface {
    return px >= POPUP_W ? "desktop" : "mobile";
}

/* ------------------------------------------------------------------ */
/* The form half                                                       */
/* ------------------------------------------------------------------ */

/**
 * The sign-in form's states, as the merchant may edit them.
 *
 * `LoginPanel` runs seven: `checking`, `known`, `phone`, `otp`, `profile`,
 * `done`, `signed-out`. Only these four are the shopper's to read and decide
 * on — the other three are transient or post-sign-in, and their copy stays
 * the platform's so a half-second interstitial cannot be turned into an
 * advert.
 */
export const FORM_STATES = ["known", "phone", "otp", "profile"] as const;
export type FormState = (typeof FORM_STATES)[number];

/**
 * Values the renderer substitutes into merchant copy.
 *
 * Written as `{mobile}` / `{name}` in the text. Two states carry a live value
 * the shopper needs — the number an OTP went to, and who they are being
 * greeted as — and a merchant rewriting those lines has to be able to say
 * WHERE the value lands, not just what surrounds it.
 *
 * A token that is missing from the copy is not restored: the merchant may
 * genuinely want a line without it, and silently appending the value would
 * make the field lie about what it renders. The editor warns instead.
 */
export const FORM_TOKENS: Record<FormState, string[]> = {
    known: ["name"],
    phone: [],
    otp: ["mobile"],
    profile: [],
};

/** One state's copy. Every field optional: absent means "platform default". */
export interface FormStateCopy {
    title?: string;
    subtitle?: string;
}

/**
 * The profile state asks for whatever this shopper is still missing, so its
 * supporting line has three readings. The merchant writes all three: a single
 * line that had to cover "your name", "your email" and "your name and email"
 * would end up saying none of them well.
 */
export interface FormProfileCopy extends FormStateCopy {
    subtitle_name?: string;
    subtitle_email?: string;
    subtitle_both?: string;
}

/** Placeholders and button labels, per state. */
export interface FormFieldCopy {
    /** `phone` — the mobile input. */
    mobile_placeholder?: string;
    /** `otp` — shown when the code box is empty. */
    code_placeholder?: string;
    /** `profile` — the three inputs. */
    name_placeholder?: string;
    email_placeholder?: string;
    /** The primary button on each state. */
    submit_label?: string;
    /** `known` — the "use another number" escape. */
    secondary_label?: string;
}

export interface SignetFormConfig {
    version: 1;
    states: Partial<Record<FormState, FormStateCopy | FormProfileCopy>>;
    fields: Partial<Record<FormState, FormFieldCopy>>;

    /**
     * Parts this store has switched off, per state.
     *
     * Separate from the copy rather than a blank string, because the two mean
     * different things: an empty box hands the line back to the platform, and
     * this removes it. A merchant who wants no subtitle would otherwise have
     * no way to say so — clearing it would restore the default.
     */
    hidden?: Partial<Record<FormState, FormHideablePart[]>>;

    /**
     * Whether this store asks for an email before it lets a shopper through.
     *
     * Merchant-owned and stored per tenant, defaulting to off. The server is
     * still what ENFORCES it — the panel runs on the shopper's machine, so a
     * requirement that lived only here would be walked straight past by
     * anything calling MintHandoff directly.
     */
    collect_email?: boolean;

    /**
     * Where the legal line points.
     *
     * Required of the merchant, not optional here: the field is typed as
     * possibly-absent because an older published document has no idea these
     * exist, and the renderer has to survive reading one. The customizer is
     * what refuses to publish without them.
     *
     * They are the store's own pages, so they belong to the store rather than
     * to a surface or a state — the same two URLs serve every step that shows
     * the line, on both a phone and a desktop.
     */
    legal?: SignetLegalLinks;
}

export interface SignetLegalLinks {
    terms_url?: string;
    privacy_url?: string;
}

/**
 * Is this a URL we are willing to send a shopper to?
 *
 * `http(s)` only, and parseable. The value travels merchant to server to
 * shopper's browser and ends up in a navigation, so `javascript:` and `data:`
 * have to be refused somewhere; refusing them HERE means every consumer gets
 * the same answer rather than each remembering to ask.
 */
export function isSafeUrl(url: unknown): url is string {
    if (typeof url !== "string" || !url.trim()) return false;
    try {
        const parsed = new URL(url, "https://storedum.com");
        return parsed.protocol === "https:" || parsed.protocol === "http:";
    } catch {
        return false;
    }
}

/** Every state's copy left to the platform, and email off. */
export function defaultFormConfig(): SignetFormConfig {
    return { version: 1, states: {}, fields: {}, hidden: {}, collect_email: false };
}

/** Has this store switched this part off on this state? */
export function isPartHidden(
    config: SignetFormConfig | undefined,
    state: FormState,
    part: FormPart,
): boolean {
    return (config?.hidden?.[state] ?? []).includes(part as FormHideablePart);
}

/**
 * Substitute `{token}` values into merchant copy.
 *
 * Unknown tokens are left standing rather than blanked: `{mobil}` rendering as
 * `{mobil}` shows the merchant their typo, where an empty gap hides it.
 */
export function fillTokens(text: string, values: Record<string, string | undefined>): string {
    return text.replace(/\{(\w+)\}/g, (whole, key: string) => {
        const value = values[key];

        return value === undefined ? whole : value;
    });
}

/* ------------------------------------------------------------------ */
/* What a store publishes                                              */
/* ------------------------------------------------------------------ */

/** One surface's half of a published config. */
export interface PublishedSurface {
    panel: SignetPanelConfig;
    form: SignetFormConfig;
}

/**
 * The document a shopper's sign-in is drawn from.
 *
 * RENDERER-SHAPED, not editor-shaped, and that distinction is the point. The
 * customizer works on a document built for editing — sections carry a layer
 * title, the background is flat, intensity is a 0-100 slider — and translates
 * it on the way out. Publishing the editor's own document instead would push
 * that translation into every consumer, and the shopper page would need to
 * import the admin's code to read it.
 *
 * So the two saved documents differ on purpose: `signet_draft` is what the
 * editor reopens, `signet_published` is this, and only this reaches a store.
 */
export interface SignetPublishedConfig {
    version: 1;
    desktop: PublishedSurface;
    mobile: PublishedSurface;

    /**
     * Store-level, not per surface: a shopper is asked for an email or is not,
     * and which device they happen to hold cannot change that.
     */
    collect_email: boolean;
}

/**
 * Is this really a published config, and not the editor's own document?
 *
 * The two are alarmingly alike — both are `{version, desktop: {panel, form},
 * mobile: {...}}` with sections inside — so "has sections" cannot tell them
 * apart, and a guard that only checked for those let the editor's shape
 * through. The background's pattern is where they genuinely differ: the editor
 * keeps it flat as a string it can put in a select, the renderer needs the
 * object it draws from. Checking the thing that actually differs is what makes
 * this a guard rather than a formality.
 */
export function isPublishedConfig(value: unknown): value is SignetPublishedConfig {
    const c = value as SignetPublishedConfig | null;
    const rendererShaped = (surface: PublishedSurface | undefined) =>
        Array.isArray(surface?.panel?.sections) &&
        typeof surface?.panel?.background?.pattern === "object";

    return !!c && typeof c === "object" && c.version === 1 && rendererShaped(c.desktop) && rendererShaped(c.mobile);
}

/**
 * The half of a published config this surface should draw.
 *
 * Falls back to desktop when a config predates the phone document, so an older
 * published record still renders rather than throwing a shopper out.
 */
export function publishedSurface(
    config: SignetPublishedConfig | null | undefined,
    layout: "desktop" | "mobile",
): PublishedSurface | null {
    if (!isPublishedConfig(config)) return null;

    return config[layout] ?? config.desktop ?? null;
}

/**
 * What a store that has never published shows a shopper.
 *
 * This is a real panel, not a placeholder. It has to be: the flag that turns
 * Signet on is a storefront setting, and nothing makes a merchant open the
 * customizer first — so this composition is what a pilot store ships on day
 * one. An earlier version left `feature` and `assurance` empty, which drew
 * five blank rows where the offer belongs and made a working modal look
 * broken.
 *
 * The composition is the design's own, and deliberately the same one the
 * customizer starts a merchant on (`initialDocument` / `initialMobileDocument`
 * in the admin). A merchant who opens the editor should recognise the panel
 * they already had rather than meet a different one.
 *
 * Every widget is left unconfigured on purpose. Each carries its own fallback
 * copy, so the panel reads as finished English without this file duplicating
 * strings that already live next to the code that draws them.
 */
export function defaultPanelConfig(surface: PanelSurface = "desktop"): SignetPanelConfig {
    const look: Pick<SignetPanelConfig, "version" | "accent" | "background"> = {
        version: 1,
        accent: "#E3A44C",
        background: { mode: "dark", tint: "neutral", pattern: { type: "halo", corner: "bottom-right" } },
    };

    // A phone gets four rows, not ten, and a section layout must total exactly
    // its surface's budget. Returning the desktop document on both surfaces
    // overflowed the mobile column by 300px — the panel scrolled, and the
    // renderer reported a problem against a document nobody had authored.
    if (surface === "mobile") {
        return {
            ...look,
            sections: [
                { id: "brand", rows: 1, widgets: [{ type: "brand.logo", rows: 1 }] },
                {
                    id: "feature",
                    rows: 3,
                    widgets: [
                        { type: "value.offer_hero", rows: 2 },
                        // Pinned, not inferred. The renderer would choose the
                        // slider on this surface anyway, but a merchant who
                        // opens the phone document should find the choice made
                        // and visible rather than hidden in a default.
                        { type: "value.unlock_ladder", rows: 1, settings: { carousel: true } },
                    ],
                },
            ],
        };
    }

    return {
        ...look,
        sections: [
            { id: "brand", rows: 2, widgets: [{ type: "brand.logo", rows: 1 }] },
            {
                id: "feature",
                rows: 5,
                widgets: [
                    { type: "value.offer_hero", rows: 3 },
                    { type: "value.unlock_ladder", rows: 2 },
                ],
            },
            {
                id: "assurance",
                rows: 2,
                widgets: [
                    { type: "trust.strip", rows: 1 },
                    { type: "trust.rating", rows: 1 },
                ],
            },
            { id: "footer", rows: 1, widgets: [{ type: "brand.powered_by", rows: 1 }] },
        ],
    };
}

/* ------------------------------------------------------------------ */
/* Editor <-> preview messages                                         */
/* ------------------------------------------------------------------ */

/**
 * What the editor is pointing at. Widgets are addressed by position, not by
 * type — a section can hold two of the same widget and they must stay
 * distinct. `background` is everything that is not a widget: clicking the
 * panel's surface selects the mode/tint/pattern layer.
 */
export type EditorTarget =
    | { kind: "widget"; section_id: string; index: number }
    | { kind: "section"; section_id: string }
    | { kind: "background" }

    /**
     * A part of the form half. `state` is which of the four is being edited;
     * `part` is which line or control inside it. The form has no document of
     * its own to index into — its structure is fixed by the component — so a
     * target names the part rather than a position.
     */
    | { kind: "form"; state: FormState; part: FormPart }

    /** The form half as a whole, selected by clicking its empty space. */
    | { kind: "form-root" };

/** The addressable parts of one form state. */
export const FORM_PARTS = ["title", "subtitle", "field", "submit", "secondary"] as const;
export type FormPart = (typeof FORM_PARTS)[number];

/**
 * The parts a merchant may switch off.
 *
 * `field` and `submit` are absent deliberately, not forgotten: without an
 * input there is nothing for a shopper to type into, and without a button
 * nothing to press. A sign-in step that cannot be completed is not a design
 * choice, so the editor does not offer it.
 *
 * `secondary` is here — "Change number" and "Use a different number" are
 * conveniences, and a shopper who loses them can still close the popup and
 * start again.
 */
export const FORM_HIDEABLE_PARTS = ["title", "subtitle", "secondary"] as const;
export type FormHideablePart = (typeof FORM_HIDEABLE_PARTS)[number];

export function isPartHideable(part: FormPart): part is FormHideablePart {
    return (FORM_HIDEABLE_PARTS as readonly string[]).includes(part);
}

export function targetsEqual(a: EditorTarget | null | undefined, b: EditorTarget | null | undefined): boolean {
    if (!a || !b) return a === b;
    if (a.kind !== b.kind) return false;
    if (a.kind === "background" || a.kind === "form-root") return true;
    if (a.kind === "section") return b.kind === "section" && a.section_id === b.section_id;
    if (a.kind === "form") return b.kind === "form" && a.state === b.state && a.part === b.part;
    return b.kind === "widget" && a.section_id === b.section_id && a.index === b.index;
}

/**
 * Editing state, owned by the EDITOR and merely mirrored by the preview.
 * The preview draws outlines and captures clicks; it never mutates the
 * document — every change round-trips through the parent, so the admin's
 * undo stack sees everything.
 */
export interface PreviewEditingState {
    enabled: boolean;
    /** The selected target, drawn with the strong outline + name chip. */
    selection?: EditorTarget | null;
    /** A target hovered in the editor's layer list, drawn with the soft outline. */
    hover?: EditorTarget | null;
}

/**
 * Editor -> preview. Sent on load and on every edit; the preview re-renders
 * the whole document each time (it is small, and diffing an edit stream is a
 * bug farm). `sample` picks which canned data set fills data-driven widgets.
 */
export interface PreviewSetMessage {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    type: "preview.set";
    config: SignetPanelConfig;
    sample?: "default" | "empty";
    editing?: PreviewEditingState;

    /**
     * Which surface to draw. The editor owns this because it owns the device
     * toggle; the preview never guesses from its own viewport, or the frame
     * would show a phone layout simply for being a narrow iframe.
     */
    device?: "desktop" | "mobile";

    /** The merchant's form copy. Absent means every state uses platform copy. */
    form?: SignetFormConfig;

    /**
     * Which form state to draw.
     *
     * The preview holds no session, so it cannot reach `otp` or `profile` by
     * signing anyone in — the editor says which one to show and the renderer
     * draws it with sample values. Absent means `phone`, the state a shopper
     * opens on.
     */
    state?: FormState;
}

/** Preview -> editor, once its listener is live. Re-send `preview.set` after. */
export interface PreviewReadyMessage {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    type: "preview:ready";
}

/**
 * What the renderer measured for one placed widget. `overflow_px > 0` is the
 * "44px over" number from the design sheets — content taller than the rows it
 * claimed. Only the real renderer can produce it, which is why validation
 * lives here and not in the admin's forms.
 */
export interface WidgetReport {
    section_id: string;
    /** Index within the section, so two widgets of one type stay distinct. */
    index: number;
    type: string;
    rows: number;
    /** Pixels of content that do not fit the claimed rows. 0 = fits. */
    overflow_px: number;

    /**
     * Pixels of content that do not fit the widget's WIDTH. 0 = fits.
     *
     * Height was measured from the start because rows are what a merchant
     * budgets. Width never was — and `.sd-widget` clips, so a widget too wide
     * for a phone did not bleed or misalign, it simply lost its right-hand end
     * silently. The sealed coupon shipped that way: the padlock and the word
     * SEALED, the only part telling a shopper what to do, were cut off.
     *
     * Reported so the customizer catches it the way it catches a widget that
     * is too tall — on the merchant's screen, before a shopper sees it.
     */
    overflow_x_px: number;
    /** The widget rendered nothing (empty data, unknown type placeholder…). */
    empty: boolean;
    /** True when we have no real renderer for `type` yet — a placeholder ran. */
    placeholder: boolean;
}

export interface SectionReport {
    id: string;
    rows: number;
    /** Rows claimed by widgets vs the section's budget. */
    rows_used: number;
    /** Widgets whose combined claim exceeds the section — editor must fix. */
    over_budget: boolean;
}

/** Preview -> editor after every render of a `preview.set`. */
export interface PreviewReportMessage {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    type: "preview:report";
    /** Echo of the config's own marker so the editor can pair reports to edits. */
    config_version: 1;
    sections: SectionReport[];
    widgets: WidgetReport[];
    /** Document-level problems (sections not summing to PANEL_ROWS, …). */
    problems: string[];
}

/**
 * Preview -> editor: the merchant clicked something in the rendered panel.
 * The preview does NOT change its own selection — it reports, the editor
 * updates its state and echoes it back in the next `preview.set`. One owner
 * of the truth; the echo is fast enough that selection feels instant.
 */
export interface PreviewSelectMessage {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    type: "preview:select";
    target: EditorTarget;
    /** Convenience for the editor's properties header. */
    widget_type?: string;
}

/** Preview -> editor: pointer entered/left a widget. `null` = left the panel. */
export interface PreviewHoverMessage {
    ns: typeof PROTOCOL_NAMESPACE;
    v: typeof PROTOCOL_VERSION;
    type: "preview:hover";
    target: EditorTarget | null;
}

export type PreviewInbound = PreviewSetMessage;
export type PreviewOutbound =
    | PreviewReadyMessage
    | PreviewReportMessage
    | PreviewSelectMessage
    | PreviewHoverMessage;

export function isPreviewSet(data: unknown): data is PreviewSetMessage {
    const d = data as PreviewSetMessage | null;
    return (
        !!d &&
        d.ns === PROTOCOL_NAMESPACE &&
        d.type === "preview.set" &&
        !!d.config &&
        Array.isArray(d.config.sections)
    );
}

export function isPreviewOutbound(data: unknown): data is PreviewOutbound {
    const d = data as PreviewOutbound | null;
    return (
        !!d &&
        d.ns === PROTOCOL_NAMESPACE &&
        (d.type === "preview:ready" ||
            d.type === "preview:report" ||
            d.type === "preview:select" ||
            d.type === "preview:hover")
    );
}

/**
 * Document-level validation shared by the editor and the preview. Layout
 * arithmetic only — anything that needs rendering (overflow, contrast) comes
 * back in the `preview:report` instead.
 */
/** `#rgb` or `#rrggbb`. The renderer puts this straight into a custom property. */
export function isHex(value: unknown): value is string {
    return typeof value === "string" && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}

export function validatePanelConfig(
    config: SignetPanelConfig,
    surface: PanelSurface = "desktop",
): string[] {
    const budget = SURFACE_ROWS[surface].rows;
    const problems: string[] = [];
    const total = config.sections.reduce((sum, s) => sum + s.rows, 0);
    if (total !== budget) {
        problems.push(`sections total ${total} rows — the panel is exactly ${budget}`);
    }
    if (config.background.tint === "custom" && !isHex(config.background.base)) {
        problems.push("custom tint has no base colour — pick one or choose a preset tint");
    }
    for (const section of config.sections) {
        const used = section.widgets.reduce((sum, w) => sum + w.rows, 0);
        if (used > section.rows) {
            problems.push(
                `section "${section.id}" holds ${used} rows of widgets in a ${section.rows}-row section`,
            );
        }
    }
    return problems;
}
